import type { Fund } from "@/data/funds";
import { groupBySector } from "@/lib/fund-single-stock-sectors";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { Link } from "react-router-dom";

interface LeveragedSingleStockSectorsProps {
  funds: Fund[];
}

/**
 * Single-stock 2x leveraged funds, grouped by the underlying's sector.
 *
 * Replaces the old flat sortable table. Renders a chip strip (one pill per
 * non-empty sector bucket) that jumps to + opens the matching accordion, then
 * one collapsible `<section>` per sector. All sections are closed by default.
 * Grouping, fixed bucket order, empty-omission, and per-bucket fund sorting all
 * live in `@/lib/fund-single-stock-sectors` — this component is pure view.
 */
export function LeveragedSingleStockSectors({ funds }: LeveragedSingleStockSectorsProps) {
  const groups = groupBySector(funds);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  if (groups.length === 0) return null;

  function toggle(key: string) {
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function openAndScroll(key: string, anchorId: string) {
    setOpen((prev) => ({ ...prev, [key]: true }));
    document.getElementById(anchorId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div>
      {/* Chip strip — jump-to + open the matching sector accordion. */}
      <div className="flex flex-wrap gap-2 px-10 max-md:px-6 py-4 bg-[var(--etf-black)]">
        {groups.map((group) => (
          <button
            key={group.key}
            type="button"
            onClick={() => openAndScroll(group.key, group.anchorId)}
            className="inline-flex items-baseline gap-1.5 px-2.5 py-1.5 rounded-[2px] bg-[var(--etf-black3)] border border-[var(--etf-border2)] cursor-pointer transition-colors duration-150 hover:border-[rgba(255,85,0,0.32)] hover:bg-[rgba(255,85,0,0.06)]"
          >
            <span className="font-[family-name:var(--font-f37bolton)] text-[13px] font-semibold text-[var(--etf-white)]">
              {group.funds.length}
            </span>
            <span className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.08em] uppercase text-[var(--etf-white3)]">
              {group.label}
            </span>
          </button>
        ))}
      </div>

      {/* One collapsible section per sector bucket. */}
      {groups.map((group) => {
        const isOpen = !!open[group.key];
        const rowsId = `${group.anchorId}-rows`;
        return (
          <section key={group.key} id={group.anchorId}>
            <button
              type="button"
              onClick={() => toggle(group.key)}
              aria-expanded={isOpen}
              aria-controls={rowsId}
              className="flex items-center justify-between w-full py-3 px-10 max-md:px-6 pl-[56px] bg-[var(--etf-black)] border-none text-left cursor-pointer transition-colors duration-150 hover:bg-[var(--etf-black2)]"
            >
              <span className="font-[family-name:var(--font-instrument)] text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--etf-white)]">
                {group.label}
              </span>
              <span className="flex items-center gap-3">
                <span className="font-[family-name:var(--font-instrument)] text-[10px] uppercase tracking-[0.08em] text-[var(--etf-white3)]">
                  {group.funds.length} {group.funds.length === 1 ? "fund" : "funds"}
                </span>
                <span
                  aria-hidden="true"
                  className={cn(
                    "inline-block text-[18px] text-[var(--etf-white3)] opacity-70 transition-transform duration-200",
                    isOpen && "rotate-90",
                  )}
                >
                  ›
                </span>
              </span>
            </button>

            {isOpen && (
              <div
                id={rowsId}
                className="grid grid-cols-2 max-[900px]:grid-cols-1 gap-px bg-[var(--etf-border)]"
              >
                {group.funds.map((fund) => (
                  <Link
                    key={fund.id}
                    to={`/${fund.id}`}
                    className="group/row flex items-center gap-3 px-5 py-3 bg-[var(--etf-black)] border-l-2 border-l-transparent no-underline transition-[background,border-color] duration-150 hover:bg-[rgba(255,85,0,0.04)] hover:border-l-[var(--etf-orange)]"
                  >
                    <span className="font-[family-name:var(--font-f37bolton)] text-[var(--etf-white)] group-hover/row:text-[var(--etf-orange)]">
                      {fund.ticker}
                    </span>
                    <span className="flex-1 min-w-0 truncate font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white2)]">
                      {fund.name}
                    </span>
                    <span className="font-[family-name:var(--font-instrument)] text-[12px] text-[var(--etf-white3)] whitespace-nowrap">
                      {fund.expenseRatio}
                    </span>
                    <span className="text-[var(--etf-white3)] group-hover/row:text-[var(--etf-orange)]">
                      →
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
